import { twMerge } from 'tailwind-merge';
import { uniqueId } from 'lodash';
import { createElement, CSSProperties, ReactNode } from 'react';

import { HtmlPreviewErrorDispatcher } from '@/errors/htmlPreview';
import { ErrorCapturer } from '@/adapters/ErrorCapturer';
import { AudioPlayer } from '@/components/ui/custom/AudioPlayer';
import { isSafeAudioSrc } from '@/utils/validators/isSafeAudioSrc';
import { isAllowedIframeSrc } from '@/utils/validators/isAllowedIframeSrc';
import { useIframeAllowlist } from '@/data/hook/useIframeAllowlist';
import {
  ALLOWED_ATTRS,
  ALLOWED_TAGS,
  isSafeId,
  isSafeUri,
  sanitizeHtmlForHtmlPreview,
  URI_ATTRS,
} from '@/utils/validators/htmlSanitizer';

export type DisplayFormat = 'book' | 'default' | 'none';

interface HtmlPreviewProps {
  testId?: string;
  html?: string;
  format?: DisplayFormat;
  className?: string;
}

export const formatClasses: Record<DisplayFormat, string> = {
  book: 'bookHtmlDisplay',
  default: 'defaultHtmlDisplay',
  none: '',
};

export const HtmlPreview = ({
  html: content = '',
  format = 'default',
  className,
  testId,
}: HtmlPreviewProps) => {
  const iframeAllowlist = useIframeAllowlist();

  const replaceHtml = (rawHtml: string): string => {
    try {
      const linkPattern = /<a href="https?:\/\//gi;
      const linkReplacement = '<a target="_blank" href="https://';

      const youtubePatterns = [
        /<oembed\s+url="https:\/\/www\.youtube\.com\/watch\?v=([\w-]{11})&amp;ab_channel=[\w%]*"><\/oembed>/gi,
        /<oembed\s+url="https:\/\/youtu\.be\/([\w-]{11})\?t=[\w-]*"><\/oembed>/gi,
        /<oembed\s+url="https:\/\/www\.youtube\.com\/watch\?v=([\w-]{11})&amp;t=[\w-]*"><\/oembed>/gi,
        /<oembed\s+url="https:\/\/www\.youtube\.com\/watch\?v=([\w-]{11})"><\/oembed>/gi,
        /<oembed\s+url="https:\/\/youtu\.be\/([\w-]{11})"><\/oembed>/gi,
        /<oembed\s+url="(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]+)&?(?:[^"]+)?"><\/oembed>/gi,
        /<oembed\s+url="(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([\w-]+)&?(?:list=[\w-]+&?)?(?:[^"]+)?"><\/oembed>/gi,
      ];

      const loomPatterns = [
        /<oembed\s+url="https:\/\/www\.loom\.com\/share\/([\w-]+)(?:\?[^"]*)?"><\/oembed>/gi,
      ];

      const replaceOembedWithIframe = (_match: string, videoId: string) => {
        return `<iframe class="videoHtmlDisplay" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`;
      };

      const replaceLoomOembedWithIframe = (_match: string, videoId: string) => {
        return `<div style="position: relative; padding-bottom: 62.5%; height: 100%;"><iframe src="https://www.loom.com/embed/${videoId}" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"></iframe></div>`;
      };

      const colorPatterns = [
        /color:\s*rgb\(0,\s*0,\s*0\)/gi,
        /color:\s*hsl\(0,\s*0%,\s*0%\)/gi,
        /color:#000000/gi,
      ];
      const colorReplacement = 'color: var(--base-content)';

      let replacedHtml = rawHtml.replace(linkPattern, linkReplacement);

      youtubePatterns.forEach(pattern => {
        replacedHtml = replacedHtml.replace(pattern, replaceOembedWithIframe);
      });

      loomPatterns.forEach(pattern => {
        replacedHtml = replacedHtml.replace(
          pattern,
          replaceLoomOembedWithIframe,
        );
      });

      colorPatterns.forEach(pattern => {
        replacedHtml = replacedHtml.replace(pattern, colorReplacement);
      });

      return replacedHtml;
    } catch (error: any) {
      const htmlPreviewError = new HtmlPreviewErrorDispatcher(error);

      const errorCapturer = new ErrorCapturer(htmlPreviewError);
      errorCapturer.dispatchError();

      return '';
    }
  };

  const toCamelCaseProperty = (property: string): string =>
    property.replace(/-([a-z])/g, (_, char) => char.toUpperCase());

  const renderNode = (node: ChildNode): ReactNode => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (!node.textContent) return null;
      return <span key={uniqueId()}>{node.textContent}</span>;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return null;

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'input') {
      const type = element.getAttribute('type');
      const insideTodoLabel = element.closest('.todo-list__label') !== null;
      if (type !== 'checkbox' || !insideTodoLabel) return null;
      return (
        <input
          key={uniqueId()}
          type="checkbox"
          disabled
          defaultChecked={element.hasAttribute('checked')}
        />
      );
    }

    if (tagName === 'label') {
      if (!element.classList.contains('todo-list__label')) return null;
      const labelChildren = Array.from(element.childNodes).map(renderNode);
      return createElement(
        'label',
        {
          key: uniqueId(),
          className: element.getAttribute('class') ?? 'todo-list__label',
        },
        ...labelChildren,
      );
    }

    if (!ALLOWED_TAGS.has(tagName)) return null;

    if (tagName === 'audio') {
      const src = element.getAttribute('src');
      if (!isSafeAudioSrc(src)) return null;
      const name = element.getAttribute('data-name') ?? undefined;
      return <AudioPlayer key={uniqueId()} src={src} name={name} />;
    }

    const props = Array.from(element.attributes).reduce((acc, attr) => {
      if (!ALLOWED_ATTRS.has(attr.name)) return acc;
      if (URI_ATTRS.has(attr.name) && !isSafeUri(attr.value)) return acc;
      if (attr.name === 'id' && !isSafeId(attr.value)) return acc;

      if (attr.name === 'class') acc['className'] = attr.value;
      else if (attr.name === 'style') acc['style'] = parseStyle(attr.value);
      else if (attr.name === 'allowfullscreen')
        acc['allowFullScreen'] = attr.value;
      else if (attr.name === 'frameborder') acc['frameBorder'] = attr.value;
      else acc[attr.name] = attr.value;

      return acc;
    }, {} as Record<string, any>);

    if (tagName === 'a') {
      props.className = twMerge(
        'text-primary hover:opacity-75 transition-all duration-300 ease-in-out',
        props.className,
      );
      props.target = '_blank';
      props.rel = 'noopener noreferrer';
    }

    if (tagName === 'iframe') {
      const src = element.getAttribute('src') ?? '';
      if (!isAllowedIframeSrc(src, iframeAllowlist)) return null;
      props.sandbox =
        'allow-scripts allow-same-origin allow-presentation allow-popups';
      props.referrerPolicy = 'no-referrer';
    }

    const children = Array.from(element.childNodes).map(renderNode);

    return createElement(
      tagName,
      { key: uniqueId(), ...props },
      ...children,
    );
  };

  const parseStyle = (style: string): CSSProperties => {
    const splitStyle = style.split(';');

    const filterStyle = splitStyle.filter(Boolean);

    const convertedStyleProps = filterStyle.reduce((cssProperties, rule) => {
      const [property, value] = rule.split(':').map(item => item.trim());

      if (property && value) {
        const camelCaseProperty = toCamelCaseProperty(property);
        (cssProperties as Record<string, string>)[camelCaseProperty] = value;
      }

      return cssProperties;
    }, {} as CSSProperties);

    return convertedStyleProps;
  };

  if (content) {
    const parser = new DOMParser();
    const dom = parser.parseFromString(
      sanitizeHtmlForHtmlPreview(replaceHtml(content)),
      'text/html',
    );

    const reactNodes = Array.from(dom.body.childNodes).map(renderNode);

    return (
      <div
        data-testid={testId}
        className={twMerge(
          'font-rubik text-wrap break-words htmlDisplay ck-content',
          formatClasses[format],
          className,
        )}
      >
        {reactNodes}
      </div>
    );
  }

  return null;
};
